/**
 * WC-07: Rewards Panel Component
 *
 * Displays available and earned rewards for the week.
 */

import React, { useMemo } from 'react';
import type { RewardDefinition, UserChallengeProgress } from '../../stores/useChallengeStore';
import './RewardsPanel.css';

/**
 * Backend rewards format (ParsedRewards object)
 */
interface ParsedRewardsFormat {
  dailyCompletion?: { type: string; amount?: number };
  streakBonuses?: Array<{ days: number; reward: { type: string; amount?: number; id?: string; name?: string } }>;
  bossDefeat?: { type: string; id?: string; name?: string };
  perfectWeek?: { type: string; id?: string; name?: string };
}

interface RewardsPanelProps {
  rewards?: RewardDefinition[] | ParsedRewardsFormat;
  progress: UserChallengeProgress | null;
}

/**
 * Transform backend rewards object to array format
 */
function normalizeRewards(rewards: RewardDefinition[] | ParsedRewardsFormat | undefined): RewardDefinition[] {
  if (!rewards) return [];

  // Already an array
  if (Array.isArray(rewards)) return rewards;

  // Transform object format to array
  const result: RewardDefinition[] = [];

  // Daily completion reward (shown for each day)
  if (rewards.dailyCompletion) {
    for (let day = 1; day <= 7; day++) {
      result.push({
        id: `daily-${day}`,
        type: 'tokens',
        name: `${rewards.dailyCompletion.amount || 10} Tokens`,
        description: `Complete Day ${day}`,
        condition: { type: 'day_complete', day },
        claimed: false
      });
    }
  }

  // Streak bonuses
  if (rewards.streakBonuses) {
    for (const bonus of rewards.streakBonuses) {
      result.push({
        id: `streak-${bonus.days}`,
        type: (bonus.reward.type as RewardDefinition['type']) || 'tokens',
        name: bonus.reward.name || `${bonus.reward.amount} ${bonus.reward.type === 'currency' ? 'Tokens' : 'Reward'}`,
        description: `${bonus.days}-day streak bonus`,
        condition: { type: 'streak', days: bonus.days },
        claimed: false
      });
    }
  }

  // Boss defeat reward (Day 7)
  if (rewards.bossDefeat) {
    result.push({
      id: rewards.bossDefeat.id || 'boss-defeat',
      type: (rewards.bossDefeat.type as RewardDefinition['type']) || 'creature_unlock',
      name: rewards.bossDefeat.name || 'Boss Defeat Reward',
      description: 'Defeat the final boss',
      condition: { type: 'boss_defeat', day: 7 },
      claimed: false
    });
  }

  // Perfect week reward
  if (rewards.perfectWeek) {
    result.push({
      id: rewards.perfectWeek.id || 'perfect-week',
      type: (rewards.perfectWeek.type as RewardDefinition['type']) || 'badge',
      name: rewards.perfectWeek.name || 'Perfect Week Badge',
      description: 'Complete all days with 3 stars',
      condition: { type: 'perfect_week' },
      claimed: false
    });
  }

  return result;
}

export const RewardsPanel: React.FC<RewardsPanelProps> = ({
  rewards: rawRewards,
  progress,
}) => {
  // Normalize rewards to array format
  const rewards = useMemo(() => normalizeRewards(rawRewards), [rawRewards]);
  const isRewardEarned = (reward: RewardDefinition): boolean => {
    if (!progress) return false;

    switch (reward.condition.type) {
      case 'day_complete': {
        const dayProgress = progress.days[reward.condition.day];
        return dayProgress?.status === 'completed';
      }
      case 'streak':
        return progress.maxStreak >= reward.condition.days;
      case 'boss_defeat': {
        const dayProgress = progress.days[reward.condition.day];
        return dayProgress?.status === 'completed';
      }
      case 'perfect_week':
        return progress.totalStars === 21; // 7 days × 3 stars
      case 'stars':
        return progress.totalStars >= reward.condition.count;
      default:
        return false;
    }
  };

  const getRewardIcon = (type: string): string => {
    switch (type) {
      case 'tokens': return '🪙';
      case 'creature_unlock': return '🦊';
      case 'badge': return '🏆';
      case 'upgrade_token': return '⬆️';
      default: return '🎁';
    }
  };

  const formatCondition = (condition: RewardDefinition['condition']): string => {
    switch (condition.type) {
      case 'day_complete':
        return `Day ${condition.day}`;
      case 'streak':
        return `${condition.days}-Day Streak`;
      case 'boss_defeat':
        return `Day ${condition.day} Boss`;
      case 'perfect_week':
        return 'Perfect Week';
      case 'stars':
        return `${condition.count} Stars`;
      default:
        return '';
    }
  };

  // Guard against undefined rewards
  if (!rewards || rewards.length === 0) {
    return (
      <div className="rewards-panel">
        <h3 className="rewards-title">Rewards</h3>
        <p className="no-rewards">No rewards available for this challenge.</p>
      </div>
    );
  }

  return (
    <div className="rewards-panel">
      <h3 className="rewards-title">Rewards</h3>
      <div className="rewards-list">
        {rewards.map((reward) => {
          const earned = isRewardEarned(reward);
          return (
            <div
              key={reward.id}
              className={`reward-item ${earned ? 'earned' : 'locked'}`}
            >
              <span className="reward-status">
                {earned ? '✅' : '⬜'}
              </span>
              <span className="reward-condition">
                {formatCondition(reward.condition)}:
              </span>
              <span className="reward-icon">
                {getRewardIcon(reward.type)}
              </span>
              <span className="reward-name">{reward.name}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
